
export interface TokenDetails {
	type: string
	token: string
	scopes: string[]
	issued: string
	expires: string
	username: string
	provider: string
}

type PartialTokenDetails = Omit<TokenDetails, "provider">

function getEnvVar(name: string) : string | undefined {
	try {
		return process?.env?.[name] ?? process?.env?.[`REACT_APP_${name}`]
	}
	catch {
		return undefined
	}
}

let _clientId = getEnvVar("CLIENT_ID") ?? ""

export function SetClientID(clientId: string) {
	//console.log(clientId)
	if(clientId) {
		_clientId = clientId
	}
}

let _allowDoorman = true
export function AllowDoormanAsAuthProvider(allow: boolean) {
	_allowDoorman = allow
}

export async function Authenticate(scopes: string[]) : Promise<TokenDetails | undefined> {
	console.assert(scopes.length <= 10, "A maximum of 10 scopes can be requested at a time")

	const tokenName = getTokenName(scopes)
	clearToken(tokenName)

	let tokenDetails : TokenDetails | undefined = undefined
	
	tokenDetails = await getTokenFromParent(scopes)
	if(!tokenDetails) {
		tokenDetails = await getTokenFromDoorman(scopes)
	}
	if(!tokenDetails) {
		tokenDetails = await getTokenFromCentralAuth(scopes)
	}

	if(tokenDetails) {
		try {
			storage.setItem(tokenName, JSON.stringify(tokenDetails))
		}
		catch {
			console.warn("Failed to store token details")
		}
	}

	return tokenDetails
}

export async function GetToken(scopes: string[], minTimeBeforeExpiryMS:number = 0) : Promise<TokenDetails|undefined> {
	const tokenDetails = getTokenFromStorage(scopes, minTimeBeforeExpiryMS)
	if(tokenDetails) {
		return tokenDetails
	}
	return await Authenticate(scopes)
}

export async function HasAccess(scopes: string[]) : Promise<string[] | undefined> {
	console.assert(scopes.length <= 10, "A maximum of 10 scopes can be requested at a time")

	let tokenDetails = getTokenFromStorage(scopes)
	if(!tokenDetails) {
		const permittedScopes = await hasAccessFromDoorman(scopes);
		if(permittedScopes) {
			return permittedScopes;
		}
	}
	if(!tokenDetails) { //Should we try the parent if we only get a partial match from doorman?
		tokenDetails = await getTokenFromParent(scopes)
	}
	return tokenDetails?.scopes
}

export async function LogOut() : Promise<void> {
	for(let i = storage.length - 1; i >= 0; --i) {
		const key = storage.key(i)
		if(key && key.startsWith(AUTH_TOKEN_PREFIX)) {
			storage.removeItem(key)
		}
	}
	
	try {
		await fetch("/auth/logout")
	}
	catch {
		return
	}
}

export async function Preauthenticate(origin: string, scopes?: string[]) : Promise<Boolean> {
	const headers : HeadersInit = {}
	if(scopes?.length) {
		const tokenDetails = await GetToken(scopes)
		if(tokenDetails) {
			headers.Authorization = `${tokenDetails.type} ${tokenDetails.token}`
		}
	}
	
	try {
		const response = await fetch(`${origin}/auth/setCookie`, {headers, credentials: 'include'})
		//console.log(`Preauth ${origin} ${(response.ok && !response.redirected) ? "successful" : "failed"}`)
		return response.ok && !response.redirected
	}
	catch {
		return false;
	}
}

class PageStorage implements Storage {
	private store : {[key: string]: string} = {}
	public fake = true;

	public get length() {
		return Object.keys(this.store).length
	}

	public key = (index: number) : string | null =>  {
		const keys = Object.keys(this.store)
		if(index >= 0 && index < keys.length) {
			return keys[index]
		}
		return null;
	}

	public getItem = (key: string) : string | null => {
		return this.store[key] ?? null;
	}

	public setItem = (key: string, value: string) => {
		this.store[key] = value
	}

	public removeItem = (key: string) => {
		if(this.store[key] !== undefined) {
			delete this.store[key]
		}
	}

	public clear = () => {
		this.store = {}
	}
}
function checkStorageAvailable(storage: Storage) : Storage | undefined {
	try {
		if(storage !== undefined) {
			const storageTestVar = "__zl_storage_test__"
			storage.setItem(storageTestVar, storageTestVar)
			storage.removeItem(storageTestVar)
			return storage
		}
	}
	catch {
	}
	return undefined
}



function detectAvailableStorage() : Storage {
	return checkStorageAvailable(window.localStorage) ?? checkStorageAvailable(window.sessionStorage) ?? new PageStorage()
}

const storage = detectAvailableStorage()

console.log("Using PageStorage: ", storage instanceof PageStorage)

const AUTH_STATE_PREFIX = 'zl_auth_state'
const AUTH_TOKEN_PREFIX = 'zl_auth_token'

const AUTH_TOKEN_PARENT_REQUEST = 'zl_auth_token_parent_request'
const AUTH_TOKEN_PARENT_RESPONSE = 'zl_auth_token_parent_response'

const AUTH_TOKEN_POPUP_RESPONSE = 'zl_auth_token_popup_response'

const AUTH_MODE_POPUP = 'popup'
const AUTH_MODE_INLINE = 'inline'

const AUTH_STATE_LIFESPAN = 30 * 60 * 1000 //30 minutes

interface CentralAuthStateData {
	name: string,
	timestamp: number,
	scopes: string[],
	provider: string
}

function parseBool(value?: string) {
	if(!value) {
		return false
	}
	return ["TRUE", "YES", "ON", "1"].includes(value.toUpperCase())
}

const CENTRAL_AUTH_ORIGINS : {[distribution: string]: string} = {
	"dev": "https://centralauth-dev.zlthunder.net",
	"live": "https://authentication.zlthunder.net"
}

function onLoad() {
	//Cleanup expired central auth state data
	const now = Date.now()
	if(storage) {
		for(let i = storage.length - 1; i >= 0; --i) {
			const key = storage.key(i)
			if(key && key.startsWith(AUTH_STATE_PREFIX)) {
				const {timestamp} = JSON.parse(storage.getItem(key) as string) as CentralAuthStateData
				if((timestamp + AUTH_STATE_LIFESPAN) < now) {
					storage.removeItem(key)
				}
			}
		}
	}

	//Clean up expired tokens or can they be refreshed?
	
	var urlParams = new URLSearchParams(window.location.search)

	//Handle Implicit authentication response
	const expectedStateKey = urlParams.get('state')
	const token = urlParams.get('access_token')
	if (expectedStateKey && token) {
		const timestamp = Date.now()
		const partialTokenDetails : PartialTokenDetails = {
			token,
			type: urlParams.get('token_type') as string,
			scopes: decodeURIComponent(urlParams.get('scope') as string).split(","),
			issued: new Date(timestamp).toISOString(),
			expires: new Date(timestamp + parseInt(urlParams.get("expires_in") as string) * 1000).toISOString(),
			username: urlParams.get("username") as string
		}

		//We've just been redirected back from auth
		if(storage instanceof PageStorage) {
			window.postMessage({event: AUTH_TOKEN_POPUP_RESPONSE, state: expectedStateKey, details: partialTokenDetails}, window.location.origin)
		}
		else {
			const expectedStateData = storage.getItem(expectedStateKey)
			if (expectedStateData) {
				const {name: tokenName, provider} = JSON.parse(expectedStateData) as CentralAuthStateData
				const tokenDetails : TokenDetails = {
					...partialTokenDetails,
					provider
				}

				storage.setItem(tokenName, JSON.stringify(tokenDetails))
				storage.removeItem(expectedStateKey)
			} else {
				console.error(`State '${expectedStateKey}' returned did not match any expected states, ignoring`)
			}
		}
		
		if(expectedStateKey.endsWith(AUTH_MODE_POPUP)) {
			window.close()
		}
		else {
			//Strip the doorman arguments from the URL?
			urlParams.delete("token_type")
			urlParams.delete("access_token")
			urlParams.delete("scope")
			urlParams.delete("expires_in")
			urlParams.delete("username")
			urlParams.delete("state")
			const newParams = urlParams.toString()
			window.history.replaceState(null, '', newParams ? `${window.location.pathname}?${urlParams}` : window.location.pathname)
		}
	}
	
	//Add handler for processing auth requests from child pages
	window.addEventListener("message", async (ev: MessageEvent) => {
		if(!ev.source || ev.source === window) {
			return
		}
		if(ev.data?.event === AUTH_TOKEN_PARENT_REQUEST) {
			(ev.source as Window).postMessage({event: AUTH_TOKEN_PARENT_RESPONSE}, ev.origin)
			
			let tokenDetails = getTokenFromStorage(ev.data.scopes)
			if(!tokenDetails) {
				tokenDetails = await getTokenFromDoorman(ev.data.scopes)
			}
			if(tokenDetails) {
				tokenDetails.provider = `parent:${window.location.origin}`
			}
			else {
				tokenDetails = await getTokenFromParent(ev.data.scopes)
			}
			
			if(ev.source) {
				(ev.source as Window).postMessage({event: AUTH_TOKEN_PARENT_RESPONSE, tokenDetails, requestUID: ev.data.requestUID}, ev.origin)
			}
		}
		else if(ev.data?.event === AUTH_TOKEN_POPUP_RESPONSE && ev.origin === window.location.origin) {
			const expectedStateKey = ev.data.state
			const expectedStateData = storage.getItem(expectedStateKey)
			if (expectedStateData) {
				const {name: tokenName, provider} = JSON.parse(expectedStateData) as CentralAuthStateData
				const tokenDetails : TokenDetails = {
					...ev.data.details,
					provider
				}

				storage.setItem(tokenName, JSON.stringify(tokenDetails))
				storage.removeItem(expectedStateKey)
			}
		}
	})
}

onLoad()

function inIframe() {
	return window.parent !== null && window.parent !== window
}

function isLocal() {
	const hostname = window.location.hostname
	return hostname === "localhost" || hostname === "127.0.0.1" || hostname.endsWith("zerolight.io")
}

function getClientId() : string {
	return _clientId
}

function getTokenName(requestedScopes: string[]) : string {
	return `${AUTH_TOKEN_PREFIX}_${getClientId()}_[${requestedScopes.sort().join(",")}]`
}

function clearToken(name: string) {
	try {
		storage.removeItem(name)
	}
	catch {
	}
}

function processTokenFromStorage(name: string, minTimeBeforeExpiryMS: number=0) {
	const tokenDetailsJsonStr = storage.getItem(name)
	if(tokenDetailsJsonStr){
		const tokenDetails = JSON.parse(tokenDetailsJsonStr) as TokenDetails
		const timestamp = Date.now();
		const expires = Date.parse(tokenDetails.expires)
		if(Number.isNaN(expires)) {
			console.warn("No expiry information available for token, ignoring")
			clearToken(name)
		}
		else if(expires < timestamp) {
			clearToken(name)
		}
		else if(expires < (timestamp + minTimeBeforeExpiryMS)) {
			console.assert(timestamp - Date.parse(tokenDetails.issued) > minTimeBeforeExpiryMS, "Requesting a longer expiry than the service has provided on a new token, likely to result in endless auth loop");
		}
		else if(tokenDetails.token) {
			return tokenDetails
		}
	}
	return undefined;
}

function getTokenFromStorage(requestedScopes: string[], minTimeBeforeExpiryMS: number=0) : TokenDetails | undefined {
	try {
		
		const exactTokenName = getTokenName(requestedScopes)
		const token = processTokenFromStorage(exactTokenName, minTimeBeforeExpiryMS)
		if(token) {
			return token
		}

		//If we don't find an exact match, search for a superset
		const keys = []
		const requiredPrefix = `${AUTH_TOKEN_PREFIX}_${getClientId()}_\\[.*\\]`
		for(let index = 0; index < storage.length; ++index) {
			const key = storage.key(index)
			if(key && key.length > exactTokenName.length && key.match(requiredPrefix)) {
				keys.push(key)
			}
		}

		//Attempt to use the token with narrowest scope,
		// Might be better if this checked number of scopes rather than textual length
		// Either way it can't guarantee one name doesn't expand to multiple
		keys.sort((l,r) => l.length - r.length)

		for(const key of keys) {
			const token = processTokenFromStorage(key, minTimeBeforeExpiryMS)
			if(token && requestedScopes.every(scope => token.scopes.includes(scope))) {
				//"security" by obfuscation - all happening locally on the client so probably not really necessary
				return {...token, scopes: requestedScopes}
			}
		}
	}
	catch {
		console.log("Cannot get cached token because storage is unavailable")
	}
	return undefined
}

function runningDoormanLocal() {
	return document.cookie.includes("doorman-local=1");
}

async function getTokenFromDoorman(scopes: string[]) : Promise<TokenDetails | undefined> {
	if(!_allowDoorman || (isLocal() && !runningDoormanLocal() && !parseBool(getEnvVar("USE_DOORMAN_LOCAL")))) {
		return undefined
	}
	
	const urlParams = new URLSearchParams(window.location.search)
	const xAccessToken = urlParams.get("x-access-token")
	if(xAccessToken) {
		const tokenDetails : TokenDetails = {
			type: "Bearer",
			token: xAccessToken,
			issued: "",
			expires: "",
			scopes: [],
			username: "",
			provider: "url"
		}
		return tokenDetails
	}

	//Try asking doorman for a token
	try {
		const request = await fetch(`/auth/getToken?scope=${encodeURIComponent(scopes.join(" "))}`)
		if(request.ok) {
			const json = await request.json()
			const tokenDetails : TokenDetails = {
				type: json.token_type ?? "Bearer",
				token: json.access_token,
				issued: new Date().toISOString(),
				expires: json.expires,
				scopes: json.scope.split(" "),
				username: json.username ?? "",
				provider: "doorman"
			}
			return tokenDetails
		}
	}
	catch(e) {
		console.warn("Failed to request a token from doorman", e)
	}
	return undefined
}

async function hasAccessFromDoorman(scopes: string[]) : Promise<string[] | undefined> {
	if(!_allowDoorman) {
		return undefined
	}

	try {
		const request = await fetch('/auth/getPermittedScope')
		if(request.ok) {
			const json = await request.json()
			const permittedScopes = json.scope?.split(" ")
			return scopes?.filter(scope => permittedScopes.includes(scope))
		}
	}
	catch(e) {
		console.warn("Failed to request the permitted scopes from doorman", e)
	}

	//Older versions of doorman don't support the getPermittedScope route so just try to get a token with the requested scopes
	//it will fail if the user only has a subset of the requested scopes
	const tokenDetails = await getTokenFromDoorman(scopes)
	return tokenDetails?.scopes
}

async function getTokenFromParent(scopes: string[]) : Promise<TokenDetails | undefined> {
	if(!inIframe()) {
		return undefined
	}

	//Try asking the parent page for a token
	let listener : ((ev: MessageEvent) => void) | undefined = undefined
	let supportedTimeout : ReturnType<typeof setTimeout> | undefined = undefined
	let responseTimeout : ReturnType<typeof setTimeout> | undefined = undefined
	try {
		const requestUID = crypto?.randomUUID?.() ?? scopes.join(" ")
		const tokenDetails = await Promise.race([
			new Promise<void>((resolve) => {
				supportedTimeout = setTimeout(() => {
					console.log("ZL AUTH: Timeout checking if parent page supports providing a token")
					supportedTimeout = undefined
					clearTimeout(responseTimeout)
					responseTimeout = undefined
					resolve()
				}, 500)
			}),
			new Promise<void>((resolve) => {
				responseTimeout = setTimeout(() => {
					console.log("ZL AUTH: Timeout getting a token from the parent page")
					responseTimeout = undefined
					resolve()
				}, 10000)
			}),
			new Promise<TokenDetails | undefined>((resolve) => {
				listener = (ev:MessageEvent) => {
					if(ev.data?.event === AUTH_TOKEN_PARENT_RESPONSE) {
						if(supportedTimeout !== undefined) {
							clearTimeout(supportedTimeout)
							supportedTimeout = undefined
						}
						if(ev.data.requestUID && ev.data.requestUID !== requestUID) {
							return
						} 
						if(ev.data.hasOwnProperty("tokenDetails")) {
							if(responseTimeout !== undefined) {
								clearTimeout(responseTimeout)
								responseTimeout = undefined
							}
							const tokenDetails = ev.data.tokenDetails as TokenDetails | undefined
							console.log("ZL AUTH: Received token from parent page", tokenDetails)
							resolve(tokenDetails)
						}
						else {
							console.log("ZL AUTH: Received confirmation of token request support from parent page", tokenDetails)
						}
					}
				}
				window.addEventListener("message", listener)
				window.parent.postMessage({event: AUTH_TOKEN_PARENT_REQUEST, scopes, requestUID}, "*")
				console.log("Sent request for auth token to parent page", scopes)
			})
		])
		if(tokenDetails) {
			return tokenDetails
		}
	}
	catch (e){
		console.error("Error attempting to get token from parent page", e)
	}
	finally {
		if(supportedTimeout !== undefined) {
			clearTimeout(supportedTimeout)
			supportedTimeout = undefined
		}
		if(responseTimeout !== undefined) {
			clearTimeout(responseTimeout)
			responseTimeout = undefined
		}
		if(listener !== undefined) {
			window.removeEventListener("message", listener)
			listener = undefined
		}
	}
	return undefined
}

let blockRedirect = false
async function getTokenFromCentralAuth(scopes: string[]) : Promise<TokenDetails | undefined> {

	const clientId = getClientId()
	if(!clientId) {
		console.error("Cannot get implicit auth without specifying a client id")
		return undefined
	}

	const tokenName = getTokenName(scopes)
	const usePopupMode = inIframe() || parseBool(getEnvVar("ALWAYS_USE_POPUP_AUTH"))
		
	const centralAuthDistribution = (getEnvVar("CENTRAL_AUTH_DISTRIBUTION") || "live").toLowerCase()
	const centralAuthOrigin = CENTRAL_AUTH_ORIGINS[centralAuthDistribution] ?? centralAuthDistribution

	const stateKey = `${AUTH_STATE_PREFIX}_${(Math.random() + 1).toString(36).substring(2, 12)}_${usePopupMode? AUTH_MODE_POPUP : AUTH_MODE_INLINE}`
	const stateData : CentralAuthStateData = {
		name: tokenName,
		timestamp: Date.now(),
		scopes,
		provider: `implicit:${centralAuthDistribution}`
	}

	const implicitFlowURL = `${centralAuthOrigin}/api/v1/implicit?response_type=token` +
				`&client_id=${clientId}` +
				`&redirect_url=${encodeURIComponent(window.location.href)}` +
				`&scope=${encodeURIComponent(scopes.join(" "))}` +
				`&state=${stateKey}`

	if(usePopupMode) {
		const tokenDetails = await new Promise((resolve: (tokenDetails: TokenDetails|undefined) => void, reject : () => void) => {
			try {
				storage.setItem(stateKey, JSON.stringify(stateData))
			}
			catch {
				console.error("Failed to store state to storage, unable to proceed with implicit auth")
				reject()
				return
			}

			let popup : Window | null = null

			const onStorageChange = (ev: StorageEvent) => {
				if(ev.key === tokenName) {
					window.addEventListener('storage', onStorageChange)
					if(popup) {
						popup.removeEventListener('close', onCloseEvent)
						popup.close()
						popup = null
					}
					if(ev.newValue) {
						resolve(JSON.parse(ev.newValue))
					}
					else {
						reject()
					}
				}
			}

			const onCloseEvent = () => {
				popup = null
				window.removeEventListener('storage', onStorageChange)
				reject()
			}

			const checkPopupBlocker = () => {
				//Is there a better way - ideally would be localised
				alert("Attempting to show an authentication dialog, has it been blocked?")
				reject()
			}

			window.addEventListener('storage', onStorageChange)

			const width = 500
			const height = 700
			const top = ((window.outerHeight / 2) + window.screenY) - (height / 2)
			const left = ((window.outerWidth / 2) + window.screenX) - (width / 2)

			
			popup = window.open(implicitFlowURL, "_blank", `width=${width},height=${height},top=${top},left=${left}`)
			if(popup) {
				popup.addEventListener('close', onCloseEvent)
				popup.focus()
				setTimeout(() => {
					if(popup === null || popup.closed) {
						checkPopupBlocker()
					}
				},
				100)
			}
			else {
				checkPopupBlocker()
			}
		})
		return tokenDetails
	}
	else if(!blockRedirect){
		blockRedirect = true
		try {
			storage.setItem(stateKey, JSON.stringify(stateData))
		}
		catch {
			console.error("Failed to store state to storage, unable to proceed with implicit auth")
			return undefined
		}
		window.location.href = implicitFlowURL
	}
	return undefined
}